import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { publishNotification } from '../../../utils/notifications/publish-notification';
import { ProjectContext } from '../../state/project-state/project-state';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Stack } from '../stack/stack';
import { TextArea } from '../text-area/text-area';
import { TextBox } from '../text-box/text-box';
import { SectionTitle } from './styles/styles';

const GeneralInfo = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { project, refreshProjecName, refreshProjectDescription } =
    React.useContext(ProjectContext);

  const [name, setName] = React.useState(project.name);
  const [companyName, setCompanyName] = React.useState<string>();
  const [description, setDescription] = React.useState(project.description);

  React.useEffect(() => {
    const load = async () => {
      const companyService = SkyMapAxiosServiceFactory.instance.createCompanyServiceV0();
      const company = await companyService.fetchProjectCompany({ query: { id: project.id } });
      setCompanyName(company.data.name);
    };

    void load();
  }, [project.id]);

  const updateProjectName = React.useCallback(async () => {
    if (project.name === name) {
      return;
    }

    const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
    await projectService.update({ path: { id: project.id }, body: { name } });
    refreshProjecName(name);
    void queryClient.invalidateQueries({ queryKey: ['getProjects'] });
    void queryClient.invalidateQueries({ queryKey: ['getBasicProjectInformationList'] });
    void queryClient.invalidateQueries({ queryKey: ['loadProject', project.id] });
    publishNotification('succ0311');
  }, [name, project.id, project.name, queryClient, refreshProjecName]);

  const updateProjectDescription = React.useCallback(async () => {
    if (project.description === description) {
      return;
    }

    const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV0();
    await projectService.update({
      path: { id: project.id },
      body: { description: description ?? '' },
    });

    refreshProjectDescription(description);
    publishNotification('succ0317');
  }, [description, project.description, project.id, refreshProjectDescription]);

  return (
    <Component>
      <SectionTitle>
        {t('projectSettings.generalInfo.sectionTitle', { ns: 'components' }).toUpperCase()}
      </SectionTitle>

      <Stack spacing={1}>
        <LabelledContainer text={t('projectSettings.generalInfo.name', { ns: 'components' })}>
          {(formElementId) => (
            <TextBox
              id={formElementId()}
              value={name}
              onBlur={() => {
                void updateProjectName();
              }}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </LabelledContainer>

        <LabelledContainer
          text={t('projectSettings.generalInfo.projectOwner', { ns: 'components' })}
        >
          <TextBox
            disabled={true}
            value={companyName ?? t('projectSettings.generalInfo.loading', { ns: 'components' })}
          />
        </LabelledContainer>

        <LabelledContainer
          text={t('projectSettings.generalInfo.description', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextArea
              id={formElementId()}
              rows={5}
              value={description}
              onBlur={() => {
                void updateProjectDescription();
              }}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </LabelledContainer>
      </Stack>
    </Component>
  );
};

const Component = styled.div``;

GeneralInfo.styled = Component;

export { GeneralInfo };
